import { TruncatedTooltip } from "@/components/shared/TruncatedTooltip";
import { Button } from "@/components/ui/button";
import {
    ThreadListItemMorePrimitive,
    ThreadListItemPrimitive,
    useAui,
    useAuiState,
} from "@assistant-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import {
    MoreHorizontalIcon,
    PencilIcon,
    PinIcon,
    PinOffIcon,
    TrashIcon,
} from "lucide-react";
import { useState, type FC } from "react";
import { Link } from "react-router";

import { useConversationClient } from "@/hooks/useConversationClient";
import { useCachedConversationDetail } from "@/hooks/useConversationDetail";
import { useOwnedConversations } from "@/hooks/useOwnedConversations";
import { usePinConversation } from "@/hooks/usePinConversation";
import {
    DEFAULT_CONVERSATION_TITLE,
    UNRESOLVED_EXTERNAL_ID,
} from "@/pages/frontend/chat/adapters/api-thread-list-adapter";
import { saveConversationClient } from "@/pages/frontend/chat/utils/conversationQueries";
import {
    EditConversationDialog,
    type EditConversationUpdates,
} from "./EditConversationDialog";

export const OwnedThreadListItem: FC = () => {
    const itemId = useAuiState(
        ({ threadListItem }) => threadListItem?.remoteId
    );
    const runtimeTitle = useAuiState(
        ({ threadListItem }) => threadListItem?.title ?? ""
    );
    const hasUnresolvedMarker = useAuiState(
        ({ threadListItem }) =>
            threadListItem?.externalId === UNRESOLVED_EXTERNAL_ID
    );
    const aui = useAui();
    const queryClient = useQueryClient();
    const detail = useCachedConversationDetail(itemId);
    const title = runtimeTitle || detail?.title || "";
    const isUnresolved = hasUnresolvedMarker && detail?.access_mode !== "owner";
    const [editOpen, setEditOpen] = useState(false);
    // Only fetch the client tag when the dialog opens; gating avoids
    // fanning out a GET /conversations/:id for every ThreadList row on
    // first render.
    const { client } = useConversationClient(editOpen ? itemId : undefined);

    // Pin state is read from the same owned-conversations cache the partition
    // buckets on, so the menu label and the row's section stay in lockstep.
    const owned = useOwnedConversations();
    const isPinned =
        owned?.items.find((c) => c.id.toString() === itemId)?.is_pinned ??
        (detail?.access_mode === "owner" && detail.is_pinned);
    const pin = usePinConversation(itemId);

    const handleSave = async (updates: EditConversationUpdates) => {
        if (updates.title !== undefined) {
            await aui.threadListItem().rename(updates.title);
        }
        if (updates.client_id !== undefined && itemId != null) {
            await saveConversationClient(
                queryClient,
                itemId,
                updates.client_id
            );
        }
    };

    return (
        <ThreadListItemPrimitive.Root className="aui-thread-list-item group hover:bg-muted focus-visible:bg-muted data-active:bg-muted relative flex h-9 items-center rounded-lg transition-colors focus-visible:outline-none">
            <ThreadListItemPrimitive.Trigger
                className="aui-thread-list-item-trigger flex h-full min-w-0 flex-1 items-center pr-10 pl-3 text-start text-sm"
                render={
                    <Link
                        to={
                            itemId
                                ? `/organizations/chat/${itemId}`
                                : "/organizations/chat"
                        }
                    />
                }
            >
                <TruncatedTooltip
                    text={title || DEFAULT_CONVERSATION_TITLE}
                    className="aui-thread-list-item-title min-w-0 flex-1"
                    tooltipSide="right"
                />
                {/* `ThreadListItemPrimitive.Title` is intentionally not used
                    here — the `title` we read via `useAuiState` above is the
                    same source the primitive reads from, and using a plain
                    span lets `TruncatedTooltip` measure overflow and decide
                    whether to mount a tooltip on hover. */}
            </ThreadListItemPrimitive.Trigger>

            {/* More-button sits absolutely over the row's right edge so the
                trigger can use the full row width for the title (the title
                truncates against the `pr-10` reservation, not against the
                button's intrinsic flex slot). */}
            {isUnresolved ? null : (
                <ThreadListItemMore
                    onEdit={() => setEditOpen(true)}
                    onTogglePin={() => pin.setPinned(!isPinned)}
                    isPinned={isPinned}
                    isPinPending={pin.isPending}
                />
            )}

            {editOpen && (
                <EditConversationDialog
                    open
                    onClose={() => setEditOpen(false)}
                    currentTitle={title}
                    currentClient={client ?? null}
                    onSave={handleSave}
                />
            )}
        </ThreadListItemPrimitive.Root>
    );
};

// Rows reach this component from the owner-only `GET /conversations` list,
// from `initialize()`, or from an adapter `fetch()` that resolved the chat as
// owned; collaborator rows render through `SharedThreadListItem`. So the
// Delete affordance is safe to render without fetching each row's
// access_mode. A row `fetch()` could not resolve gets no menu at all.
const ThreadListItemMore: FC<{
    onEdit: () => void;
    onTogglePin: () => void;
    isPinned: boolean;
    isPinPending: boolean;
}> = ({ onEdit, onTogglePin, isPinned, isPinPending }) => {
    return (
        <ThreadListItemMorePrimitive.Root>
            <ThreadListItemMorePrimitive.Trigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className="aui-thread-list-item-more text-muted-foreground hover:text-foreground data-[state=open]:bg-accent data-[state=open]:text-foreground absolute top-1/2 right-1.5 size-7 -translate-y-1/2 p-0 transition-colors"
                >
                    <MoreHorizontalIcon className="size-4" />
                    <span className="sr-only">More options</span>
                </Button>
            </ThreadListItemMorePrimitive.Trigger>
            <ThreadListItemMorePrimitive.Content
                side="bottom"
                align="start"
                className="aui-thread-list-item-more-content bg-popover text-popover-foreground z-50 min-w-32 overflow-hidden rounded-md border p-1 shadow-md"
            >
                <ThreadListItemMorePrimitive.Item
                    onClick={onEdit}
                    className="aui-thread-list-item-more-item hover:bg-accent focus:bg-accent flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none select-none"
                >
                    <PencilIcon className="size-4" />
                    Edit
                </ThreadListItemMorePrimitive.Item>
                <ThreadListItemMorePrimitive.Item
                    onClick={onTogglePin}
                    disabled={isPinPending}
                    className="aui-thread-list-item-more-item hover:bg-accent focus:bg-accent flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none select-none"
                >
                    {isPinned ? (
                        <PinOffIcon className="size-4" />
                    ) : (
                        <PinIcon className="size-4" />
                    )}
                    {isPinned ? "Unpin" : "Pin"}
                </ThreadListItemMorePrimitive.Item>
                <ThreadListItemPrimitive.Delete
                    render={
                        <ThreadListItemMorePrimitive.Item className="aui-thread-list-item-more-item text-destructive hover:bg-destructive/10 hover:text-destructive focus:bg-destructive/10 focus:text-destructive flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none select-none" />
                    }
                >
                    <TrashIcon className="size-4" />
                    Delete
                </ThreadListItemPrimitive.Delete>
            </ThreadListItemMorePrimitive.Content>
        </ThreadListItemMorePrimitive.Root>
    );
};
